import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Camera, Loader2, Plus, Search, ScanLine } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { shrinkImage } from '@/components/quotes/AIMediaCapture';
import { BarcodeScannerDialog } from '@/components/quotes/BarcodeScannerDialog';
import { pickCatalogName, pickLocalizedField } from '@/i18n/localizeItem';
import { isProductBarcode } from '@/lib/barcodeKinds';
import { parseQty } from '@/lib/stockQty';
import { showApiError } from '@/lib/apiErrors';
import { majorToMinor } from '@/lib/quoteMoney';

const API_URL = process.env.REACT_APP_API_URL;
const LOCALES = { tr: 'tr-TR', en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' };
const TYPES = ['in', 'out', 'count'];
// Label text is small, so this keeps more detail than a job site photo.
const LABEL_MAX_EDGE = 2400;


/**
 * Scan → act loop for the stock page.
 *   scan   → camera / hand scanner
 *   action → stock in / out / count for the found product
 *   assign → unknown barcode: pick the product it belongs to (once)
 *   create → unknown barcode, product not in the catalog yet: add it here
 * "Save & scan next" goes straight back to the camera, which makes a
 * shelf-by-shelf count quick.
 */
export function StockScanFlow({ open, onClose, onChanged }) {
  const { t, i18n } = useTranslation('quotes');
  const lang = (i18n.language || 'tr').split('-')[0];
  const locale = LOCALES[lang] || 'tr-TR';
  const fmt = (n) => new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(Number(n) || 0);
  const name = (row) => pickCatalogName(row, lang);
  const unit = (row) => pickLocalizedField(row, 'unit', lang);

  const [step, setStep] = useState(null); // 'scan' | 'action' | 'assign' | 'create' | null
  const [item, setItem] = useState(null);
  const [code, setCode] = useState('');
  const [type, setType] = useState('out');
  const [qty, setQty] = useState('1');
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState({ name: '', brand: '', model: '', unit: '', price: '' });
  const [secondary, setSecondary] = useState(false);
  const [reading, setReading] = useState(false);
  const labelInput = useRef(null);

  useEffect(() => {
    if (open) setStep('scan');
    else setStep(null);
  }, [open]);

  const close = () => {
    setStep(null);
    onClose();
  };

  const startAction = (found, nextType = type) => {
    setItem(found);
    setType(nextType);
    setQty(nextType === 'count' ? '' : '1');
    setStep('action');
  };

  const loadCatalog = async () => {
    const { data: list } = await axios.get(`${API_URL}/api/quote/stock`, { params: { scope: 'all', page_size: 1000 } });
    setCatalog(list.items || []);
  };

  const onDetected = async (scanned, info) => {
    setSecondary(Boolean(info?.secondary));
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/stock/lookup`, { params: { code: scanned } });
      if (data.found) {
        startAction(data.item);
      } else {
        setCode(data.code || scanned);
        setSearch('');
        setStep('assign');
        if (!catalog) await loadCatalog();
      }
    } catch (err) {
      showApiError(err, t);
      setStep('scan');
    }
  };

  const assign = async (row) => {
    try {
      const { data } = await axios.patch(`${API_URL}/api/quote/stock/items/${row.id}`, { barcode: code });
      toast.success(t('stock.scan.assignedToast', { name: name(data) }));
      setCatalog(null);
      startAction(data);
    } catch (err) {
      showApiError(err, t);
    }
  };

  const openCreate = () => {
    setDraft({ name: search.trim(), brand: '', model: '', unit: t('form.unitDefault'), price: '' });
    setStep('create');
  };

  const readLabel = async (file) => {
    if (!file) return;
    // From the camera screen nothing is scanned yet, so the label decides everything.
    const fromCamera = step === 'scan';
    const scannedCode = fromCamera ? '' : code;
    setReading(true);
    try {
      const body = new FormData();
      body.append('image', await shrinkImage(file, LABEL_MAX_EDGE));
      body.append('language', lang);
      const { data } = await axios.post(`${API_URL}/api/quote/stock/label-scan`, body);
      // The scanned code may be a serial number: the label's own barcode is better.
      if (data.barcode && data.barcode !== scannedCode && !isProductBarcode(scannedCode)) {
        const { data: found } = await axios.get(`${API_URL}/api/quote/stock/lookup`, { params: { code: data.barcode } });
        if (found.found) {
          startAction(found.item);
          return;
        }
        setCode(data.barcode);
        setSecondary(false);
      } else if (fromCamera) {
        setCode('');
      }
      setDraft((prev) => {
        const base = step === 'create' ? prev : { ...prev, unit: t('form.unitDefault'), price: '', brand: '', model: '' };
        return { ...base, name: data.name, brand: data.brand || base.brand, model: data.model || base.model };
      });
      setStep('create');
      toast.success(t('stock.scan.labelRead'));
    } catch (err) {
      showApiError(err, t);
    } finally {
      setReading(false);
      if (labelInput.current) labelInput.current.value = '';
    }
  };

  const createItem = async () => {
    const itemName = draft.name.trim();
    const price = (draft.price.trim() || '0').replace(',', '.');
    if (!itemName) {
      toast.error(t('catalog.form.validationName'));
      return;
    }
    if (!/^\d+(?:\.\d{1,2})?$/.test(price)) {
      toast.error(t('catalog.form.validationPrice'));
      return;
    }
    setSaving(true);
    let created = null;
    try {
      const { data: profile } = await axios.get(`${API_URL}/api/quote/profile`, { params: { include_logo: false } });
      const { data } = await axios.post(`${API_URL}/api/quote/catalog`, {
        item_type: 'product',
        name: itemName,
        brand: draft.brand.trim() || null,
        model: draft.model.trim() || null,
        unit: draft.unit.trim(),
        unit_price_minor: majorToMinor(price),
        currency: (profile?.currency || 'TRY').toUpperCase(),
      });
      created = data;
      const { data: tracked } = await axios.patch(`${API_URL}/api/quote/stock/items/${created.id}`, {
        barcode: code, track_stock: true,
      });
      setCatalog(null);
      toast.success(t('stock.scan.createdToast', { name: name(tracked) }));
      // A new product has no stock yet, so the first scan is a stock entry.
      startAction(tracked, 'in');
    } catch (err) {
      showApiError(err, t);
      if (created) {
        // The product exists but has no barcode: let the user pick it instead
        // of creating it twice.
        await loadCatalog().catch(() => setCatalog([]));
        setStep('assign');
      }
    } finally {
      setSaving(false);
    }
  };

  const save = async (next) => {
    const value = parseQty(qty);
    if (value === null || value < 0 || (type !== 'count' && value <= 0)) {
      toast.error(t(type === 'count' ? 'stock.validation.qtyZero' : 'stock.validation.qty'));
      return;
    }
    setSaving(true);
    try {
      if (type === 'count') {
        await axios.post(`${API_URL}/api/quote/stock/count`, {
          entries: [{ item_id: item.id, qty: value }], note: t('stock.countNote'),
        });
      } else {
        const { data } = await axios.post(`${API_URL}/api/quote/stock/movements`, {
          item_id: item.id, type, qty: value,
        });
        if (data.went_negative) toast.warning(t('stock.negativeWarning', { name: name(item) }));
      }
      toast.success(t('stock.scan.savedToast', { name: name(item) }));
      onChanged?.();
      if (next) setStep('scan');
      else close();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  const matches = useMemo(() => {
    const q = search.trim().toLocaleLowerCase(locale);
    const rows = catalog || [];
    if (!q) return rows.slice(0, 50);
    return rows.filter((r) => [r.name, r.name_en, r.sku, r.brand, r.model]
      .some((v) => (v || '').toLocaleLowerCase(locale).includes(q))).slice(0, 50);
  }, [catalog, search, locale]);

  const labelButton = (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      onClick={() => labelInput.current?.click()}
      disabled={reading || saving}
      data-testid={`scan-label-button-${step}`}
    >
      {reading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}
      {t(reading ? 'stock.scan.labelReading' : 'stock.scan.labelPhoto')}
    </Button>
  );

  return (
    <>
      <input
        ref={labelInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => readLabel(e.target.files?.[0])}
        data-testid="scan-label-input"
      />

      <BarcodeScannerDialog open={step === 'scan'} onClose={close} onDetected={onDetected}>
        {labelButton}
      </BarcodeScannerDialog>

      <Dialog open={step === 'action'} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-md">
          {item && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-6">{name(item)}</DialogTitle>
                <DialogDescription>
                  {t('stock.currentQty', { value: fmt(item.stock_qty), unit: unit(item) })}
                  {item.barcode ? ` · ${item.barcode}` : ''}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1" role="tablist">
                {TYPES.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => { setType(k); if (k === 'count') setQty(''); else if (!qty) setQty('1'); }}
                    className={`rounded-lg py-2 text-sm font-medium ${type === k ? 'bg-white shadow text-[#1A1A2E]' : 'text-slate-500'}`}
                    data-testid={`scan-type-${k}`}
                  >
                    {t(`stock.scan.types.${k}`)}
                  </button>
                ))}
              </div>
              <div>
                <Label>{type === 'count' ? t('stock.correct.realQty') : t('stock.movement.qty')}</Label>
                <Input
                  autoFocus
                  inputMode="decimal"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') save(true); }}
                  className="text-lg"
                  data-testid="scan-qty"
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => save(false)} disabled={saving}>{t('common.save')}</Button>
                <Button onClick={() => save(true)} disabled={saving} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="scan-save-next">
                  <ScanLine className="mr-2 h-4 w-4" /> {t('stock.scan.saveNext')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={step === 'assign'} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('stock.scan.unknownTitle')}</DialogTitle>
            <DialogDescription>{t('stock.scan.unknownHelp', { code })}</DialogDescription>
          </DialogHeader>
          {secondary && (
            <p className="text-sm text-amber-700" data-testid="scan-secondary-warning">{t('stock.scan.secondaryWarning')}</p>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('stock.searchPlaceholder')}
              className="pl-9"
              data-testid="scan-assign-search"
            />
          </div>
          {labelButton}
          <ul className="max-h-[35vh] divide-y divide-[#F1F5F9] overflow-y-auto">
            {catalog === null ? (
              <li className="py-6 text-center text-sm text-[#595959]">{t('common.loading')}</li>
            ) : matches.length === 0 ? (
              <li className="py-6 text-center text-sm text-[#94A3B8]">{t(catalog.length === 0 ? 'stock.scan.noProducts' : 'stock.emptyFiltered')}</li>
            ) : matches.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => assign(row)}
                  className="flex w-full items-center gap-3 px-1 py-2.5 text-left hover:bg-slate-50"
                  data-testid={`scan-assign-${row.id}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-[#1A1A2E]">{name(row)}</span>
                    <span className="block truncate text-xs text-[#94A3B8]">
                      {[row.brand, row.model, row.sku].filter(Boolean).join(' · ')}
                      {row.barcode ? ` · ${t('stock.scan.hasBarcode')}` : ''}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setStep('scan')}>{t('stock.scan.scanAgain')}</Button>
            <Button onClick={openCreate} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="scan-create-open">
              <Plus className="mr-2 h-4 w-4" /> {t('stock.scan.addNew')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={step === 'create'} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('stock.scan.addNew')}</DialogTitle>
            <DialogDescription>{code ? t('stock.scan.addNewHelp', { code }) : t('stock.scan.addNewHelpNoCode')}</DialogDescription>
          </DialogHeader>
          {labelButton}
          <div className="space-y-3">
            <div>
              <Label>{t('catalog.form.name')}</Label>
              <Input
                autoFocus
                maxLength={200}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                data-testid="scan-create-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('catalog.form.brand')}</Label>
                <Input
                  maxLength={100}
                  value={draft.brand}
                  onChange={(e) => setDraft({ ...draft, brand: e.target.value })}
                  data-testid="scan-create-brand"
                />
              </div>
              <div>
                <Label>{t('catalog.form.model')}</Label>
                <Input
                  maxLength={100}
                  value={draft.model}
                  onChange={(e) => setDraft({ ...draft, model: e.target.value })}
                  data-testid="scan-create-model"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('catalog.form.unit')}</Label>
                <Input
                  maxLength={40}
                  value={draft.unit}
                  onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                  data-testid="scan-create-unit"
                />
              </div>
              <div>
                <Label>{t('catalog.form.unitPrice')}</Label>
                <Input
                  inputMode="decimal"
                  value={draft.price}
                  onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                  data-testid="scan-create-price"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setStep('assign')} disabled={saving || reading}>{t('common.back')}</Button>
            <Button onClick={createItem} disabled={saving || reading} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="scan-create-save">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
